import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Wallet, Sparkles, ArrowDownToLine, CheckCircle2, Clock, ExternalLink, RefreshCw, QrCode } from "lucide-react";
import DepositDialog from "../../components/panel/DepositDialog";
import WalletBonusCard from "../../components/panel/WalletBonusCard";
import OrderHistory from "../../components/panel/OrderHistory";
import { SiTether, SiCircle } from "react-icons/si";
import { usePanelT } from "../../hooks/usePanelT";

const CURRENCIES = [
    { code: "USDT_TRC20", Icon: SiTether, color: "#26A17B", label: "USDT (TRC-20)", hintKey: "w.lowestFee" },
    { code: "USDT_ERC20", Icon: SiTether, color: "#26A17B", label: "USDT (ERC-20)", hint: "Ethereum" },
    { code: "USDC_ERC20", Icon: SiCircle, color: "#2775CA", label: "USDC (ERC-20)", hint: "Ethereum" },
];

function formatUsd(n) {
    return `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortHash(h) {
    if (!h) return "—";
    return h.length > 18 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h;
}

function DepositCard({ currency }) {
    const { t } = usePanelT();
    const [open, setOpen] = useState(false);
    const net = currency.code.endsWith("TRC20") ? "TRC-20" : "ERC-20";
    return (
        <>
            <button onClick={() => setOpen(true)} data-testid={`deposit-button-${currency.code}`} className="bg-card border border-white/[0.08] hover:border-primary/40 active:scale-[0.99] rounded-2xl p-4 md:p-5 transition-all text-left group flex md:block items-center gap-4">
                <div className="w-11 h-11 md:w-10 md:h-10 shrink-0 rounded-xl bg-white/[0.04] border border-white/10 grid place-items-center md:mb-3" style={{ color: currency.color }}>
                    <currency.Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="font-display font-semibold">{currency.code.split("_")[0]} <span className="text-muted-foreground font-normal text-sm">· {net}</span></p>
                    <p className="text-[11px] font-mono text-muted-foreground tracking-wide mt-0.5 truncate">{currency.hintKey ? t(currency.hintKey) : currency.hint}</p>
                </div>
                <span className="hidden md:inline-flex items-center gap-1 text-[11px] font-mono text-primary mt-3 opacity-0 group-hover:opacity-100 transition-opacity"><QrCode className="w-3.5 h-3.5" /> {t("w.qrAddress")}</span>
                <ArrowDownToLine className="w-4 h-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors md:hidden" />
            </button>
            <DepositDialog currency={currency} open={open} onOpenChange={setOpen} />
        </>
    );
}

function DepositStatusBadge({ d }) {
    const { t } = usePanelT();
    if (d.status === "credited") {
        return (
            <span data-testid={`deposit-status-${d.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                <CheckCircle2 className="w-3 h-3" /> {t("w.credited")}
            </span>
        );
    }
    return (
        <span data-testid={`deposit-status-${d.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">
            <Clock className="w-3 h-3" /> {t("w.confirming")} {d.confirmations}/{d.confirmations_required}
        </span>
    );
}

export default function PanelWallet() {
    const { t } = usePanelT();
    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [deposits, setDeposits] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (manual = false) => {
        if (manual) setRefreshing(true);
        try {
            const [w, t, d] = await Promise.allSettled([
                api.get("/me/wallet"),
                api.get("/me/transactions"),
                api.get("/me/deposits"),
            ]);
            if (w.status === "fulfilled") setWallet(w.value.data);
            if (t.status === "fulfilled") setTransactions(t.value.data);
            if (d.status === "fulfilled") setDeposits(d.value.data);
        } finally {
            if (manual) setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(() => load(false), 30000);
        return () => clearInterval(id);
    }, [load]);

    const pendingDeposits = deposits.filter((d) => d.status !== "credited");

    return (
        <div data-testid="panel-wallet" className="space-y-6 md:space-y-8">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="font-display font-bold text-2xl md:text-4xl tracking-tight">{t("w.title")}</h1>
                    <p className="text-muted-foreground mt-1 text-sm hidden sm:block">
                        {t("w.sub")}
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => load(true)}
                    data-testid="wallet-refresh"
                    className="h-10 border-white/10 bg-white/[0.02] hover:bg-white/[0.06] shrink-0"
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                    <span className="hidden sm:inline ml-2">{t("refresh")}</span>
                </Button>
            </div>

            {/* Balance hero */}
            <div className="grid md:grid-cols-3 gap-3 md:gap-4">
                <div data-testid="wallet-balance-card" className="bg-card border border-primary/30 rounded-2xl p-5 md:p-6 md:col-span-1 relative overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(158 42% 46%), transparent)" }} />
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.10] to-transparent pointer-events-none" />
                    <div className="relative">
                        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 md:mb-3">
                            <Wallet className="w-3.5 h-3.5" /> {t("w.usdBalance")}
                        </div>
                        <div className="font-display font-black text-4xl md:text-4xl tabular-nums" data-testid="wallet-usd-balance">{wallet ? formatUsd(wallet.usd_balance) : "—"}</div>
                        {pendingDeposits.length > 0 && (
                            <p className="text-xs text-yellow-300 mt-2" data-testid="wallet-pending-note">
                                {t("w.pending", { n: pendingDeposits.length })}
                            </p>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:contents">
                    <div className="bg-card border border-highlight/25 rounded-2xl p-4 md:p-6">
                        <div className="flex items-center gap-2 text-[11px] md:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 md:mb-3">
                            <Sparkles className="w-3.5 h-3.5 text-highlight" /> {t("w.cashback")}
                        </div>
                        <div className="font-display font-black text-2xl md:text-4xl text-highlight tabular-nums">{wallet ? formatUsd(wallet.cashback_balance) : "—"}</div>
                    </div>
                    <div className="bg-card border border-white/[0.07] rounded-2xl p-4 md:p-6">
                        <div className="text-[11px] md:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 md:mb-3">{t("w.monthly")}</div>
                        <div className="font-display font-black text-2xl md:text-4xl tabular-nums">{wallet ? formatUsd(wallet.monthly_spend) : "—"}</div>
                    </div>
                </div>
            </div>

            {/* Top-up bonus */}
            <WalletBonusCard />

            {/* Deposit options */}
            <div>
                <h3 className="font-display font-semibold text-lg mb-3">{t("w.cryptoTopup")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {CURRENCIES.map((c) => (
                        <DepositCard key={c.code} currency={c} />
                    ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3" data-testid="wallet-payment-monitoring-note">
                    {t("w.monitorNote")}
                </p>
            </div>

            {/* My deposits */}
            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden" data-testid="wallet-deposits">
                <div className="px-4 md:px-6 py-4 border-b border-white/5 flex items-center justify-between">
                    <h3 className="font-display font-semibold text-lg">{t("w.myDeposits")}</h3>
                    <span className="text-[11px] font-mono text-muted-foreground">{deposits.length}</span>
                </div>
                {deposits.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-10 text-center px-4" data-testid="wallet-deposits-empty">
                        {t("w.noDeposits")}
                    </p>
                ) : (
                    <div className="divide-y divide-white/5">
                        {deposits.map((d) => (
                            <div key={d.id} data-testid={`deposit-row-${d.id}`} className="px-4 md:px-6 py-3.5 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-medium text-sm">{d.currency.replace("_", " ")}</p>
                                        <DepositStatusBadge d={d} />
                                    </div>
                                    <p className="text-[11px] text-muted-foreground font-mono mt-1 flex items-center gap-1.5">
                                        {new Date(d.credited_at || d.first_seen_at).toLocaleString()}
                                        {d.explorer_url && (
                                            <a href={d.explorer_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-primary hover:underline" data-testid={`deposit-tx-link-${d.id}`}>
                                                {shortHash(d.tx_hash)} <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className={`font-display font-semibold text-base md:text-lg tabular-nums ${d.status === "credited" ? "text-highlight" : "text-yellow-300"}`}>
                                        +{formatUsd(d.amount_usd)}
                                    </p>
                                    <p className="text-[10px] font-mono text-muted-foreground">{d.amount_crypto.toFixed(2)} {d.currency.split("_")[0]}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <OrderHistory onChanged={() => load(false)} />

            {/* Transactions */}
            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                <div className="px-4 md:px-6 py-4 border-b border-white/5">
                    <h3 className="font-display font-semibold text-lg">{t("w.txHistory")}</h3>
                </div>
                {transactions.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-10 text-center">{t("w.noTx")}</p>
                ) : (
                    <div className="divide-y divide-white/5">
                        {transactions.map((tx) => {
                            const negative = tx.type === "spend" || tx.type === "ad_account_topup" || Number(tx.amount) < 0;
                            return (
                                <div key={tx.id} className="px-4 md:px-6 py-3.5 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm">{t(`tx.${tx.type}`) === `tx.${tx.type}` ? tx.type.replace(/_/g, " ") : t(`tx.${tx.type}`)}</p>
                                        <p className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
                                            {new Date(tx.created_at).toLocaleString()}
                                            {tx.note ? ` · ${tx.note}` : ""}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className={`font-display font-semibold text-base md:text-lg tabular-nums ${negative ? "text-foreground/85" : "text-highlight"}`}>
                                            {negative ? "-" : "+"}{formatUsd(Math.abs(tx.amount))}
                                        </p>
                                        <p className="text-[10px] font-mono text-muted-foreground tracking-wide">{tx.currency} · {t(`st.${tx.status}`)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
